import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./editAnnonce.css";

const StatutBadge = ({ statut }) => {
  const base = {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontWeight: 700,
    textTransform: "capitalize",
  };
  if (statut === "valide")   return <span style={{ ...base, background: "#e6f6ea", color: "#1e7f3b" }}>validée</span>;
  if (statut === "refuse")   return <span style={{ ...base, background: "#fde8e8", color: "#b91c1c" }}>refusée</span>;
  if (statut === "publiee")  return <span style={{ ...base, background: "#e7efff", color: "#1d4ed8" }}>publiée</span>;
  return <span style={{ ...base, background: "#fff7e6", color: "#a35f00" }}>en attente</span>;
};

/* ===== Helpers anti-crash horaires ===== */

// Convertit n'importe quelle valeur en "HH:MM"
const toTime = (v) => {
  if (typeof v === "string" && v.includes(":")) return v;
  if (typeof v === "number" && !Number.isNaN(v)) {
    const h = Math.floor(v / 60);
    const m = v % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  return "00:00";
};

// Rendu robuste pour workingHours
const renderWorkingHours = (wh) => {
  if (!Array.isArray(wh)) return null;

  return (
    <ul>
      {wh.map((h, i) => {
        const isOpen = !!h?.isOpen;

        // Nouveau format: h.slots = [{start, end}, ...]
        const hasSlots = Array.isArray(h?.slots) && h.slots.length > 0;
        const slotsStr = hasSlots
          ? h.slots
              .map((s) => `${toTime(s?.start)} - ${toTime(s?.end)}`)
              .join(" / ")
          : null;

        // Ancien format: h.start / h.end
        const legacyStr =
          h?.start || h?.end ? `${toTime(h?.start)} - ${toTime(h?.end)}` : null;

        return (
          <li key={i}>
            {h?.day || `Jour ${i + 1}`} : {isOpen ? (slotsStr || legacyStr || "Ouvert") : "Fermé"}
          </li>
        );
      })}
    </ul>
  );
};

/* ===== Base API (prod: même domaine / sinon ENV) ===== */
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  "";
const API_BASE = RAW
  ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
  : (typeof window !== "undefined" && window.location.origin) || "";

const EditAnnonce = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const userId = localStorage.getItem("userId");
  const token  = localStorage.getItem("token");

  const [annonces, setAnnonces] = useState([]);
  const [selectedProfil, setSelectedProfil] = useState("principal"); // “annonce globale”
  const [loading, setLoading] = useState(true);

  // Headers (token si présent)
  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  // -------- fetch annonces (avec token + 401) ----------
  const fetchAnnonces = useCallback(async () => {
    if (!userId) {
      setAnnonces([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // ✅ Route: /by-user/:userId
      const res = await fetch(`${API_BASE}/api/annonces/by-user/${userId}`, {
        headers: authHeaders,
        credentials: "include",
      });
      if (res.status === 401) {
        navigate("/auth/login");
        return;
      }
      const data = await res.json().catch(() => null);
      // Toujours un array
      setAnnonces(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) {
      console.error("❌ Erreur de chargement des annonces :", err);
      setAnnonces([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, userId, authHeaders, navigate]);

  useEffect(() => {
    fetchAnnonces();
    // auto-refresh toutes les 10s pour voir la validation admin évoluer
    const t = setInterval(fetchAnnonces, 10000);
    return () => clearInterval(t);
  }, [fetchAnnonces]);

  // -------- support mode=create pour forcer la création ----------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const forceCreate =
      sessionStorage.getItem("forceCreateAnnonce") === "true" ||
      params.get("mode") === "create";

    if (!forceCreate) return;
    sessionStorage.removeItem("forceCreateAnnonce");

    if (loading) return;

    const a = Array.isArray(annonces) ? annonces[0] : null;

    if (a && a._id) {
      navigate(`/pro/listing?id=${a._id}`);
    } else {
      navigate("/pro/listing");
    }
  }, [location.search, annonces, loading, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette annonce ?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/annonces/${id}`, {
        method: "DELETE",
        headers: authHeaders,
        credentials: "include",
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.error || "Suppression échouée");
      }
      alert("✅ Annonce supprimée");
      fetchAnnonces();
    } catch (err) {
      console.error("❌ Erreur lors de la suppression :", err);
      alert("❌ " + (err.message || "Erreur lors de la suppression"));
    }
  };

  const publier = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/annonces/${id}/publier`, {
        method: "PATCH",
        headers: authHeaders,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erreur de publication");
      }
      alert("✅ Annonce publiée !");
      fetchAnnonces();
    } catch (e) {
      alert("❌ " + e.message);
    }
  };

  const refresh = () => fetchAnnonces();

  if (loading) {
    return (
      <div className="annonce-form">
        <h2 className="annonce-title">Mes Annonces</h2>
        <p style={{ color: "#4b3a2f", textAlign: "center" }}>Chargement en cours...</p>
      </div>
    );
  }

  if (!annonces || annonces.length === 0) {
    return (
      <div className="annonce-form">
        <h2 className="annonce-title">Mes Annonces</h2>
        <p style={{ color: "#4b3a2f", textAlign: "center" }}>Aucune annonce trouvée.</p>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <button
            onClick={() => navigate("/pro/listing")}
            style={{
              cursor: "pointer",
              backgroundColor: "#6c6bc6",
              border: "none",
              color: "#fff",
              padding: "10px 16px",
              borderRadius: 8,
              fontWeight: "bold",
            }}
          >
            Créer une annonce
          </button>
        </div>
      </div>
    );
  }

  // Une annonce par user
  const annonce = annonces[0];

  const profilsList = [
    { id: "principal", name: "Profil Principal" },
    ...(annonce.profils
      ? annonce.profils.map((p, i) => ({
          id: String(i),
          name: p.name ? `${p.name} – ${p.profession}` : `Profil ${i + 1}`,
        }))
      : []),
  ];

  let profilToDisplay;
  if (selectedProfil === "principal") {
    profilToDisplay = {
      ...annonce,
      profils: annonce.profils,
    };
  } else if (annonce.profils && annonce.profils[Number(selectedProfil)]) {
    profilToDisplay = annonce.profils[Number(selectedProfil)];
  }

  const statut = annonce?.statut || "en_attente";
  const commentaire = annonce?.commentaireAdmin || "";

  return (
    <div className="annonce-form">
      <h2 className="annonce-title">Mes Annonces</h2>

      {/* Bandeau statut */}
      <div style={{ marginBottom: 16 }}>
        <strong>Statut :</strong> <StatutBadge statut={statut} />
        {statut === "refuse" && commentaire && (
          <div
            style={{
              marginTop: 8,
              color: "#b91c1c",
              background: "#fde8e8",
              padding: 10,
              borderRadius: 10,
            }}
          >
            <strong>Motif du refus :</strong> {commentaire}
          </div>
        )}
        {statut === "valide" && (
          <div
            style={{
              marginTop: 8,
              color: "#1e7f3b",
              background: "#ebf9ef",
              padding: 10,
              borderRadius: 10,
            }}
          >
            ✅ Validée par l’administration — vous pouvez publier.
          </div>
        )}
        {statut === "en_attente" && (
          <div
            style={{
              marginTop: 8,
              color: "#a35f00",
              background: "#fff7e6",
              padding: 10,
              borderRadius: 10,
            }}
          >
            ⏳ Votre annonce est en cours de vérification.
          </div>
        )}
        {statut === "publiee" && (
          <div
            style={{
              marginTop: 8,
              color: "#1d4ed8",
              background: "#e7efff",
              padding: 10,
              borderRadius: 10,
            }}
          >
            🎉 Votre annonce est en ligne.
          </div>
        )}
      </div>

      {/* Sélecteur de profil */}
      <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
        <label htmlFor="profilSelect" style={{ fontWeight: "bold", color: "#4b3a2f" }}>
          Choisir un profil :
        </label>
        <select
          id="profilSelect"
          value={selectedProfil}
          onChange={(e) => setSelectedProfil(e.target.value)}
          style={{
            padding: "8px 16px",
            borderRadius: 6,
            border: "1px solid #c2b6ab",
            background: "#fcf7ef",
            fontSize: 16,
          }}
        >
          {profilsList.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Infos annonce / profil */}
      <div
        className="day-row"
        style={{ borderBottom: "1px solid #e0d6cb", paddingBottom: "10px", marginBottom: "10px" }}
      >
        <p>
          <strong>Entreprise :</strong> {annonce.nameCompany}
        </p>
        <p>
          <strong>Description :</strong> {annonce.description || "Non renseignée"}
        </p>
        <p>
          <strong>Adresse :</strong> {annonce.adresCompany}
        </p>
        <p>
          <strong>Code postal :</strong> {annonce.postalCode}
        </p>
        <p>
          <strong>Téléphone :</strong> {annonce.phoneNumber}
        </p>

        {selectedProfil === "principal" ? (
          <>
            {profilToDisplay?.profils && profilToDisplay.profils.length > 0 && (
              <div>
                <strong>Profils & Activités :</strong>
                <ul>
                  {profilToDisplay.profils.map((prof, pi) => (
                    <li key={pi}>
                      <strong style={{ color: "#876d57" }}>{prof.profession} :</strong>
                      <ul>
                        {prof.activities && prof.activities.length > 0 ? (
                          prof.activities.map((a, ai) => (
                            <li key={ai}>
                              {a.name} – {a.durationHours}h{a.durationMinutes}m – {a.price}€
                            </li>
                          ))
                        ) : (
                          <li>Aucune activité</li>
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <strong>Profession :</strong> {profilToDisplay?.profession}
            </div>
            {profilToDisplay?.activities && profilToDisplay.activities.length > 0 && (
              <div>
                <strong>Activités :</strong>
                <ul>
                  {profilToDisplay.activities.map((a, i) => (
                    <li key={i}>
                      {a.name} – {a.durationHours}h{a.durationMinutes}m – {a.price}€
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {profilToDisplay?.workingHours && (
              <div>
                <strong>Horaires :</strong>
                {renderWorkingHours(profilToDisplay.workingHours)}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            style={{
              cursor: "pointer",
              backgroundColor: "#6c6bc6",
              border: "none",
              color: "#fff",
              padding: "10px 16px",
              borderRadius: 8,
              fontWeight: "bold",
            }}
            onClick={() => {
              if (annonce && annonce._id) {
                navigate(`/pro/listing?id=${annonce._id}`);
              } else {
                navigate("/pro/listing");
              }
            }}
          >
            Modifier
          </button>

          {statut === "valide" && (
            <button
              onClick={() => publier(annonce._id)}
              style={{
                cursor: "pointer",
                backgroundColor: "#2563eb",
                border: "none",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: 8,
                fontWeight: "bold",
              }}
            >
              Publier
            </button>
          )}

          <button
            onClick={refresh}
            style={{
              cursor: "pointer",
              backgroundColor: "#e5e7eb",
              border: "none",
              color: "#111827",
              padding: "10px 16px",
              borderRadius: 8,
              fontWeight: "bold",
            }}
          >
            Rafraîchir
          </button>

          <div
            className="delete-button"
            onClick={() => handleDelete(annonce._id)}
            style={{ padding: "10px 16px", borderRadius: 8, cursor: "pointer" }}
          >
            Supprimer
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAnnonce;
